import { useCallback, useEffect, useMemo, useState } from "react";
import { Pressable, TextInput, type LayoutChangeEvent } from "react-native";
import { useUnistyles } from "react-native-unistyles";
import { View, Text } from "@src/components";
import type { ScenarioConfigItem } from "../hooks/use-scenario-configs";
import { usePutScenarioConfig } from "../hooks/use-scenario-configs";
import { formatBearing } from "../utils/format-bearing";
import type { ConfigScenario } from "../utils/config-scenarios";
import { MapCanvas, type AssetMarker, type MapMarkerRow } from "./MapCanvas.web";

interface ConfigMapPanelProps {
  tenantId?: string;
  /** All the selected site's scenarios (drives pins + the selected form). */
  scenarios: ConfigScenario[];
  /** Located assets in the site, as context markers. */
  assetMarkers: AssetMarker[];
  /**
   * Coordinates the map should fit on load. The screen supplies its centring
   * chain here (located assets → configured pins → empty = AU/NZ default).
   */
  focusCoordinates: { lat: number; lng: number }[];
  /** URL-selected scenario, or null for a site-only view (form disabled). */
  selectedScenarioUuid: string | null;
  /** Called when the operator clicks a scenario pin on the map. */
  onSelectScenario: (scenarioUuid: string) => void;
}

/**
 * Section 1 of the config page: the map plus the pin/bearing/friendly-name form.
 *
 * This is the config-page counterpart of the old `ScenarioMap.web.tsx`, but with
 * two deliberate differences settled in the design doc (§4b):
 *   - Selection is **URL-driven** (the parent owns `selectedScenarioUuid`), not
 *     an internal `editingUuid`. Clicking a pin or a list row just changes the
 *     URL, which flows back in as the selected scenario.
 *   - The embedded positioned/unpositioned lists are **gone** — that job now
 *     belongs to the dedicated list page and Section 2's scenario list.
 *
 * The save invariants are preserved verbatim from the old flow: a blank bearing
 * clears direction (`null`), we never silently seed `0` onto an existing pin,
 * and clearing the coordinate clears the direction too.
 */
export function ConfigMapPanel({
  tenantId,
  scenarios,
  assetMarkers,
  focusCoordinates,
  selectedScenarioUuid,
  onSelectScenario,
}: ConfigMapPanelProps) {
  const { theme } = useUnistyles();
  const { putConfig, isPending } = usePutScenarioConfig();

  // MapCanvas draws a pin for every scenario that has a coordinate; build its
  // config lookup straight from the site scenarios' persisted config.
  const configByUuid = useMemo<Record<string, ScenarioConfigItem>>(() => {
    const map: Record<string, ScenarioConfigItem> = {};
    for (const s of scenarios) {
      map[s.scenarioUuid] = {
        scenarioUuid: s.scenarioUuid,
        coordinate: s.coordinate,
        direction: s.direction,
        friendlyName: s.friendlyName,
      };
    }
    return map;
  }, [scenarios]);

  const mapRows = useMemo<MapMarkerRow[]>(
    () =>
      scenarios.map((s) => ({
        scenarioUuid: s.scenarioUuid,
        scenarioName: s.scenarioName,
        cameraDisplayName: s.assetCameraLabel,
      })),
    [scenarios],
  );

  const selected = useMemo(
    () => scenarios.find((s) => s.scenarioUuid === selectedScenarioUuid) ?? null,
    [scenarios, selectedScenarioUuid],
  );

  const [draftLat, setDraftLat] = useState("");
  const [draftLng, setDraftLng] = useState("");
  const [draftDir, setDraftDir] = useState("");
  const [draftName, setDraftName] = useState("");
  const [draftExcluded, setDraftExcluded] = useState(false);

  // Re-seed the form whenever the selected scenario changes (URL-driven). Same
  // bearing-seeding rules as the old flow: existing bearing shows through; a
  // brand-new pin (no coordinate) seeds "0" so the arrow appears pointing north
  // the instant the operator clicks the map; an already-pinned scenario with no
  // bearing stays blank so saving can't silently introduce `direction: 0`.
  useEffect(() => {
    if (!selected) {
      setDraftLat("");
      setDraftLng("");
      setDraftDir("");
      setDraftName("");
      setDraftExcluded(false);
      return;
    }
    setDraftLat(selected.coordinate ? selected.coordinate.lat.toFixed(6) : "");
    setDraftLng(selected.coordinate ? selected.coordinate.lng.toFixed(6) : "");
    if (selected.direction !== undefined) {
      setDraftDir(formatBearing(selected.direction));
    } else if (!selected.coordinate) {
      setDraftDir("0");
    } else {
      setDraftDir("");
    }
    setDraftName(selected.friendlyName ?? "");
    setDraftExcluded(selected.excluded ?? false);
  }, [selected]);

  const handleMapClick = useCallback((lngLat: { lat: number; lng: number }) => {
    setDraftLat(lngLat.lat.toFixed(6));
    setDraftLng(lngLat.lng.toFixed(6));
    // Don't touch `draftDir` here — the seed effect is the single place that
    // decides the default bearing. Overwriting a deliberately-cleared bearing
    // with "0" would turn an intended `direction: null` save into a save of 0.
  }, []);

  const handleDirectionDrag = useCallback((degrees: number) => {
    setDraftDir(formatBearing(degrees));
  }, []);

  // Live draft pin mirroring exactly what a save will persist: a blank bearing
  // renders no arrow (the save sends `direction: null`), so the visual never
  // misleadingly points north for a scenario that has no bearing.
  const draftPin = useMemo(() => {
    if (!selected) return null;
    const lat = Number.parseFloat(draftLat);
    const lng = Number.parseFloat(draftLng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    const trimmed = draftDir.trim();
    if (trimmed === "") return { coordinate: { lat, lng } };
    const dir = Number.parseFloat(trimmed);
    if (!Number.isFinite(dir)) return { coordinate: { lat, lng } };
    const safeDir = ((dir % 360) + 360) % 360;
    return { coordinate: { lat, lng }, direction: safeDir };
  }, [selected, draftLat, draftLng, draftDir]);

  const saveEdit = useCallback(async () => {
    if (!selected) return;
    const latTrimmed = draftLat.trim();
    const lngTrimmed = draftLng.trim();
    const dirTrimmed = draftDir.trim();
    const nameTrimmed = draftName.trim();
    const excludedChanged = draftExcluded !== (selected.excluded ?? false);
    const bothEmpty = latTrimmed === "" && lngTrimmed === "";
    const bothFilled = latTrimmed !== "" && lngTrimmed !== "";
    if (!bothEmpty && !bothFilled) return;

    // Direction is only meaningful with a coordinate. bothFilled + typed → save
    // normalised; bothFilled + blank → clear (`null`); bothEmpty (coord cleared)
    // → also clear (the backend enforces this defensively, but sending it keeps
    // the optimistic UI consistent).
    let directionPatch: number | null | undefined;
    if (bothFilled) {
      if (dirTrimmed === "") {
        directionPatch = null;
      } else {
        const dir = Number.parseFloat(dirTrimmed);
        if (!Number.isFinite(dir)) return;
        directionPatch = ((dir % 360) + 360) % 360;
      }
    } else if (bothEmpty) {
      directionPatch = null;
    }

    try {
      if (bothEmpty) {
        const hadCoordinate = selected.coordinate !== undefined;
        const hadFriendlyName = (selected.friendlyName ?? "").trim() !== "";
        const hadDirection = selected.direction !== undefined;
        // Allow an exclude-only save even when nothing else changed, so toggling
        // Exclude on an unpinned scenario isn't silently discarded.
        if (!hadCoordinate && !hadFriendlyName && !hadDirection && nameTrimmed === "" && !excludedChanged) return;
        await putConfig(selected.siteId, selected.assetCameraKey, selected.scenarioUuid, {
          coordinate: hadCoordinate ? null : undefined,
          direction: hadDirection ? null : undefined,
          friendlyName: nameTrimmed || null,
          excluded: draftExcluded,
        });
      } else {
        const lat = Number.parseFloat(latTrimmed);
        const lng = Number.parseFloat(lngTrimmed);
        if (Number.isNaN(lat) || Number.isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) return;
        const hadName = (selected.friendlyName ?? "").trim() !== "";
        const hadDirection = selected.direction !== undefined;
        // Only emit a `direction: null` clear when there is something to clear —
        // saves a no-op REMOVE on brand-new pins.
        const directionPayload: Record<string, number | null> = {};
        if (directionPatch !== undefined && (directionPatch !== null || hadDirection)) {
          directionPayload["direction"] = directionPatch;
        }
        await putConfig(selected.siteId, selected.assetCameraKey, selected.scenarioUuid, {
          coordinate: { lat, lng },
          ...directionPayload,
          friendlyName: nameTrimmed || (hadName ? null : undefined),
          excluded: draftExcluded,
        });
      }
    } catch {
      // Form stays as-is so the operator can retry; usePutScenarioConfig.error
      // surfaces the failure if wired.
    }
  }, [selected, draftLat, draftLng, draftDir, draftName, draftExcluded, putConfig]);

  const disabled = !selected;

  // Responsive reflow for the lat/lng/direction row. We measure the row's own
  // width (not the window) because this panel sits inside WebLayout beside a
  // collapsible sidebar, so window breakpoints would be wrong. As space
  // shrinks: Direction drops to its own full-width row first, then Longitude,
  // ending in a fully-stacked (1,1,1) layout.
  const [rowWidth, setRowWidth] = useState(0);
  const onRowLayout = useCallback((e: LayoutChangeEvent) => {
    setRowWidth(e.nativeEvent.layout.width);
  }, []);
  // 0 = not yet measured → assume wide (avoids a stacked flash on first paint).
  // Thresholds are against the row's inner width. Three fields need ~290px to
  // sit comfortably (≈90px each + two 10px gaps); below ~460px we drop to two-up
  // (Direction on its own line), and below ~320px everything stacks.
  const stackAll = rowWidth > 0 && rowWidth < 320;
  const stackDirection = rowWidth > 0 && rowWidth < 460;

  // Friendly-name + Exclude sit on their own measured row. Exclude reflows to
  // its own full-width line first when the row can't hold both comfortably
  // (friendly name needs ~260px, Exclude ~120px + one gap).
  const [nameRowWidth, setNameRowWidth] = useState(0);
  const onNameRowLayout = useCallback((e: LayoutChangeEvent) => {
    setNameRowWidth(e.nativeEvent.layout.width);
  }, []);
  const stackName = nameRowWidth > 0 && nameRowWidth < 400;

  const inputStyle = {
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    borderRadius: theme.spacing.md,
    paddingHorizontal: theme.spacing.lg,
    paddingVertical: theme.spacing.md,
    color: disabled ? theme.colors.textMuted : theme.colors.text,
    backgroundColor: disabled ? theme.colors.surfaceCard : "transparent",
  } as const;

  return (
    <View style={{ gap: theme.spacing.lg }}>
      <MapCanvas
        rows={mapRows}
        configByUuid={configByUuid}
        tenantId={tenantId}
        selectedScenarioUuid={selectedScenarioUuid}
        onScenarioSelect={onSelectScenario}
        assetMarkers={assetMarkers}
        selectedAssetId={selected ? String(selected.assetId) : null}
        focusCoordinates={focusCoordinates}
        onMapClick={selected ? handleMapClick : undefined}
        draftPin={draftPin}
        onDraftDirectionChange={selected ? handleDirectionDrag : undefined}
        suppressedScenarioUuid={draftPin ? selectedScenarioUuid : null}
        height={420}
      />

      <View
        style={{
          padding: theme.spacing["3xl"],
          borderRadius: theme.spacing.md,
          borderWidth: 1,
          borderColor: theme.colors.borderCard,
          backgroundColor: theme.colors.surfaceCard,
          gap: theme.spacing.lg,
        }}
      >
        <Text textStyle="sm/semibold" color="text">
          {selected ? `Configure — ${selected.backendScenarioName}` : "Select a scenario to configure"}
        </Text>
        <Text textStyle="xs/regular" color="textMuted">
          {selected ?
            "Click anywhere on the map to drop a pin, or type latitude and longitude below. Drag the white handle on the pin (or type a value in Direction) to set the way the scenario is facing. The friendly name is what shows everywhere in this app for this scenario."
          : "Pick a scenario from the list to place its pin, set its bearing, and give it a friendly name."}
        </Text>

        <View flexDirection="row" gap="lg" style={{ flexWrap: "wrap" }} onLayout={onNameRowLayout}>
          {/* Friendly name spans ~2 columns (like lat+lng together); Exclude
              takes 1 column (like Direction) and is the first to reflow onto its
              own line when the row is squished. */}
          <View
            style={{
              flexGrow: 2,
              flexShrink: 1,
              flexBasis: stackName ? "100%" : 260,
              minWidth: 0,
            }}
          >
            <Text textStyle="xs/regular" color="textMuted">
              Friendly name
            </Text>
            <TextInput
              value={draftName}
              onChangeText={setDraftName}
              editable={!disabled}
              placeholder={selected?.backendScenarioName ?? ""}
              placeholderTextColor={theme.colors.textMuted}
              style={inputStyle}
            />
          </View>
          <View
            style={{
              flexGrow: 1,
              flexShrink: 1,
              flexBasis: stackName ? "100%" : 120,
              minWidth: 0,
            }}
          >
            <Text textStyle="xs/regular" color="textMuted">
              Report
            </Text>
            <Pressable
              onPress={() => setDraftExcluded((v) => !v)}
              disabled={disabled}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: draftExcluded, disabled }}
              accessibilityLabel="Exclude from report"
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: theme.spacing.sm,
                borderWidth: 1,
                borderColor: theme.colors.borderCard,
                borderRadius: theme.spacing.md,
                paddingHorizontal: theme.spacing.lg,
                paddingVertical: theme.spacing.md,
                backgroundColor: disabled ? theme.colors.surfaceCard : "transparent",
                opacity: disabled ? 0.5 : 1,
              }}
            >
              {/* Small square that fills in when excluded — a lightweight
                  checkbox that avoids pulling in a new switch dependency. */}
              <View
                style={{
                  width: theme.spacing.xl,
                  height: theme.spacing.xl,
                  borderRadius: theme.spacing.xs,
                  borderWidth: 1,
                  borderColor: draftExcluded ? theme.colors.status.error : theme.colors.borderCard,
                  backgroundColor: draftExcluded ? theme.colors.status.error : "transparent",
                }}
              />
              <Text textStyle="xs/regular" color={disabled ? "textMuted" : "text"}>
                Exclude from report
              </Text>
            </Pressable>
          </View>
        </View>

        <View flexDirection="row" gap="lg" style={{ flexWrap: "wrap" }} onLayout={onRowLayout}>
          <View style={{ flexGrow: 1, flexShrink: 1, flexBasis: stackAll ? "100%" : 120, minWidth: 0 }}>
            <Text textStyle="xs/regular" color="textMuted">
              Latitude
            </Text>
            <TextInput
              value={draftLat}
              onChangeText={setDraftLat}
              editable={!disabled}
              // No placeholder when nothing is selected — the empty form should
              // read as genuinely blank, not as pre-filled example values.
              placeholder={disabled ? "" : "-33.8688"}
              placeholderTextColor={theme.colors.textMuted}
              inputMode="decimal"
              style={inputStyle}
            />
          </View>
          <View style={{ flexGrow: 1, flexShrink: 1, flexBasis: stackAll ? "100%" : 120, minWidth: 0 }}>
            <Text textStyle="xs/regular" color="textMuted">
              Longitude
            </Text>
            <TextInput
              value={draftLng}
              onChangeText={setDraftLng}
              editable={!disabled}
              placeholder={disabled ? "" : "151.2093"}
              placeholderTextColor={theme.colors.textMuted}
              inputMode="decimal"
              style={inputStyle}
            />
          </View>
          <View
            style={{
              flexGrow: 1,
              flexShrink: 1,
              // Direction is the first to break to its own line: full width once
              // the row can't comfortably hold three, then it (and the others)
              // stack fully at the narrowest widths.
              flexBasis: stackDirection ? "100%" : 120,
              minWidth: 0,
            }}
          >
            <Text textStyle="xs/regular" color="textMuted">
              Direction (°)
            </Text>
            <TextInput
              value={draftDir}
              onChangeText={setDraftDir}
              editable={!disabled}
              placeholder={disabled ? "" : "0"}
              placeholderTextColor={theme.colors.textMuted}
              inputMode="numeric"
              style={inputStyle}
            />
          </View>
        </View>

        <View flexDirection="row" justifyContent="flex-end">
          <Pressable
            onPress={saveEdit}
            disabled={disabled || isPending}
            style={{
              paddingHorizontal: theme.spacing["3xl"],
              paddingVertical: theme.spacing.md,
              backgroundColor: theme.colors.icon,
              borderRadius: theme.spacing.md,
              opacity: disabled || isPending ? 0.5 : 1,
            }}
          >
            <Text textStyle="sm/semibold" color="background">
              {isPending ? "Saving…" : "Save"}
            </Text>
          </Pressable>
        </View>
      </View>
    </View>
  );
}
